/**
 * Public legal document page. Privacy and Terms are separate routes.
 * `/legal` redirects (hash `#terms` → `/terms`, otherwise `/privacy`).
 */

import { Link, Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { GrantonomyLogo } from "@/components/g-logo";
import { MarkdownBody } from "@/components/markdown-body";
import { PublicLayout } from "@/components/public-layout";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { LegalMarkdown } from "@/shared/legal-documents";

interface LegalResponse {
  privacy: LegalMarkdown;
  terms: LegalMarkdown;
}

type LegalKind = "privacy" | "terms";

function pickLocale(body: LegalMarkdown | undefined, language: string): string {
  if (!body) return "";
  return language.startsWith("en") ? body.en : body.de;
}

export function LegalLegacyRedirect() {
  const { hash } = useLocation();
  return <Navigate to={hash === "#terms" ? "/terms" : "/privacy"} replace />;
}

export function PrivacyPage() {
  return <LegalDocumentPage kind="privacy" />;
}

export function TermsPage() {
  return <LegalDocumentPage kind="terms" />;
}

function LegalDocumentPage({ kind }: { kind: LegalKind }) {
  const { t, i18n } = useTranslation();
  const legal = useQuery({
    queryKey: ["legal-documents"],
    queryFn: () => api.get<LegalResponse>("/legal"),
  });

  const markdown = pickLocale(legal.data?.[kind], i18n.language).trim();
  const emptyKey = kind === "privacy" ? "legal.emptyPrivacy" : "legal.emptyTerms";

  return (
    <PublicLayout>
      <div className="container-page max-w-3xl py-10">
        <GrantonomyLogo />
        <p className="mt-8 text-xs font-medium uppercase tracking-widest text-muted-foreground">
          {t("legal.kicker")}
        </p>
        <nav className="mt-4 flex flex-wrap gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <Link
            to="/privacy"
            className={cn(
              "hover:text-foreground hover:underline underline-offset-2",
              kind === "privacy" && "text-foreground underline",
            )}
          >
            {t("legal.linkPrivacy")}
          </Link>
          <span aria-hidden="true">·</span>
          <Link
            to="/terms"
            className={cn(
              "hover:text-foreground hover:underline underline-offset-2",
              kind === "terms" && "text-foreground underline",
            )}
          >
            {t("legal.linkTerms")}
          </Link>
        </nav>

        <section
          className="mt-10"
          data-testid={kind === "privacy" ? "legal-privacy" : "legal-terms"}
        >
          {legal.isLoading ? (
            <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : markdown ? (
            <MarkdownBody markdown={markdown} />
          ) : (
            <p className="text-sm text-muted-foreground">{t(emptyKey)}</p>
          )}
        </section>
      </div>
    </PublicLayout>
  );
}
